/**
 * PendingSignatureBanner — the one thing on the summary that is not a number.
 *
 * A partner opens this app to look at money. A Vehicle Schedule waiting for their signature
 * is the only item that is time-sensitive AND actionable, so it sits above everything else
 * on the overview and it says exactly what it wants: which document, and one button.
 *
 * It reads `get_my_pending_signatures`, which is zero-argument and session-scoped — so the
 * banner is right even for a partner holding several vehicles, and it disappears the
 * moment the signature lands rather than when a page happens to be refetched.
 */

import { useNavigate } from 'react-router-dom';
import { PenLine } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { documentLabel, SIGNING_DISABLED } from '@/lib/esign';
import { usePendingSignatures } from '@/hooks/useAgreements';

export default function PendingSignatureBanner() {
    const navigate = useNavigate();
    const { data } = usePendingSignatures(!SIGNING_DISABLED);

    const pending = data ?? [];
    if (SIGNING_DISABLED || pending.length === 0) return null;

    const next = pending[0];
    const label = documentLabel(next.title, next.doc_type);
    const extra = pending.length - 1;

    return (
        <div className="flex flex-col gap-3 rounded-xl border border-foreground/20 bg-foreground/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
                <div className="rounded-lg bg-foreground p-2 text-background">
                    <PenLine className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold tracking-tight">Action required</p>
                    <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                        Your {label} is ready to sign
                        {extra > 0
                            ? ` · ${extra} other document${extra === 1 ? '' : 's'} also waiting`
                            : ''}
                        .
                    </p>
                </div>
            </div>
            <Button
                className="shrink-0 sm:w-auto"
                onClick={() => navigate(`/sign/${next.document_id}`)}
            >
                Review &amp; Sign
            </Button>
        </div>
    );
}
